import { AxiosResponse } from 'axios';

import { makeLogger } from '@cast/utils';

import {
  MiddlewareController,
  EndpointMiddleware,
  AbstractApiClient,
  EndpointFn,
  MiddlewareFn,
  GlobalMiddleware,
  MiddlewareArgs,
} from './types';

const log = makeLogger('Middleware');

const resolveAxiosResponse = <T>(
  data: T,
  responseBase?: AxiosResponse<T>
): Promise<AxiosResponse<T>> => {
  return Promise.resolve({
    ...(responseBase || {}),
    data,
  } as AxiosResponse<T>);
};

type MiddlewareRegistry<CLIENT extends AbstractApiClient> = Array<
  {
    id: string;
  } & (
    | { middleware: EndpointMiddleware<CLIENT> }
    | { globalMiddleware: GlobalMiddleware }
  )
>;

type MiddlewareWithId = {
  id: string;
  middleware: MiddlewareFn;
};

type GetMiddlewaresArgs<CLIENT extends AbstractApiClient> = {
  feature: string;
  endpoint: string;
  args: unknown[];
  orig: EndpointFn;
  registry: MiddlewareRegistry<CLIENT>;
};
const getMiddlewaresFor = <CLIENT extends AbstractApiClient>({
  feature,
  endpoint,
  args,
  orig,
  registry,
}: GetMiddlewaresArgs<CLIENT>): MiddlewareWithId[] => {
  return registry
    .map((m) => {
      if (
        'globalMiddleware' in m &&
        m.globalMiddleware.matcher({
          orig,
          args,
          feature,
          endpoint,
          resolve: resolveAxiosResponse,
        })
      ) {
        return { id: m.id, middleware: m.globalMiddleware.middleware };
      } else if ('middleware' in m) {
        return { id: m.id, middleware: m.middleware[feature]?.[endpoint] };
      }
      return undefined;
    })
    .filter((m) => !!m?.middleware) as MiddlewareWithId[];
};

type BuildMiddlewarePipelineArgs<
  FN extends (...args: unknown[]) => PromiseLike<unknown> = (
    ...args: unknown[]
  ) => PromiseLike<unknown>
> = {
  feature: string;
  endpoint: string;
  middlewares: MiddlewareWithId[];
  orig: FN;
  args?: unknown[];
};
export const buildMiddlewarePipeline = <FN extends EndpointFn = EndpointFn>({
  feature,
  endpoint,
  middlewares,
  orig,
}: BuildMiddlewarePipelineArgs<FN>): EndpointFn => {
  const currentMiddleware = middlewares[0];
  if (!currentMiddleware) {
    return async (...args: Parameters<FN>) => await orig(...args);
  }

  return async (...args: Parameters<FN>) => {
    let called: 'next' | 'reject' | 'resolve' | 'orig' | undefined;
    log(
      `Executing: ${currentMiddleware.id}; Feature: ${feature}; Endpoint: ${endpoint}`
    );
    const result = await currentMiddleware.middleware({
      next: async (...argOverrides) => {
        called = 'next';
        return await buildMiddlewarePipeline({
          feature,
          endpoint,
          middlewares: middlewares.slice(1),
          orig,
          args: argOverrides.length ? argOverrides : args,
        })(...(argOverrides.length ? argOverrides : args));
      },
      reject: (error): ReturnType<FN> => {
        called = 'reject';
        throw error;
      },
      resolve: (arg, base) => {
        called = 'resolve';
        return resolveAxiosResponse(arg, base);
      },
      orig: (...args: unknown[]) => {
        called = 'orig';
        return orig(...args);
      },
      args,
      feature,
      endpoint,
    });
    log(
      `Middleware execution completed: ${currentMiddleware.id}, called: ${called}. Feature: ${feature}; Endpoint: ${endpoint}`,
      result
    );
    return result;
  };
};

export const setUpMiddleware = <CLIENT extends AbstractApiClient>(
  apiClient: CLIENT
): {
  apiClient: CLIENT;
  middlewareController: MiddlewareController<CLIENT>;
} => {
  let middlewareRegistry: MiddlewareRegistry<CLIENT> = [];

  const apiWithProxy: AbstractApiClient = {};

  // Actually, we could accept any object as apiClient and scan it recursively
  // However, this approach results in simpler and more typesafe API. As we have a strict structure of apiClient, no need to do that now
  for (const featureKey in apiClient) {
    apiWithProxy[featureKey] = {};
    const featureEndpoints = apiClient[featureKey];

    for (const endpointKey in featureEndpoints) {
      apiWithProxy[featureKey][endpointKey] = (...args) => {
        const orig = featureEndpoints[endpointKey];

        const lastArg = args.at(-1);
        const middlewareConfig =
          !!lastArg &&
          typeof lastArg === 'object' &&
          'middlewareArgs' in lastArg
            ? (lastArg.middlewareArgs as MiddlewareArgs)
            : undefined;

        const skipMiddlewares = middlewareConfig?.skipMiddleware?.filter(
          (id) => typeof id === 'string'
        );

        const _middlewares = getMiddlewaresFor({
          feature: featureKey,
          endpoint: endpointKey,
          orig,
          args,
          registry: middlewareRegistry,
        });

        const middlewares = skipMiddlewares
          ? _middlewares.filter(({ id }) => !skipMiddlewares.includes(id))
          : _middlewares;

        return buildMiddlewarePipeline({
          feature: featureKey,
          endpoint: endpointKey,
          orig,
          middlewares,
        })(...args);
      };
    }
  }

  const middlewareController: MiddlewareController<CLIENT> = {
    addMiddleware: (id, middleware) => {
      log(`Adding middleware: ${id}`);
      if (middlewareRegistry.some((middleware) => middleware.id === id)) {
        return;
      }
      middlewareRegistry.unshift({ id, middleware });
    },
    addGlobalMiddleware: (id: string, globalMiddleware) => {
      log(`Adding global middleware: ${id}`);
      middlewareRegistry.unshift({ id, globalMiddleware });
    },
    removeMiddleware: (id) => {
      log(`Removing middleware: ${id}`);
      middlewareRegistry = middlewareRegistry.filter((p) => p.id !== id);
    },
  };

  return { apiClient: apiWithProxy as CLIENT, middlewareController };
};
